import { API, toFullUrl } from '../config/api';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Headphones, ArrowLeft } from 'lucide-react';
import CommentSection from '../components/CommentSection';
import LikeButton from '../components/LikeButton';
import FireButton from '../components/FireButton';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { BeatPlayer } from '../components/BeatPlayer';

const normalizeAudioUrl = (url) => {
  if (!url) return url;
  if (url.startsWith('/api/')) return `${window.location.origin}${url}`;
  const filesMatch = url.match(/(\/api\/files\/.+)$/);
  if (filesMatch) return `${window.location.origin}${filesMatch[1]}`;
  return url;
};

export default function BeatsPage() {
  const [beats, setBeats] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});
  const [checkingOut, setCheckingOut] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/beats`).catch(() => ({ data: [] })),
      axios.get(`${API}/site-settings`).catch(() => ({ data: {} }))
    ]).then(([beatsRes, settingsRes]) => {
      setBeats(beatsRes.data);
      setSiteSettings(settingsRes.data);
    });
  }, []);

  const handleBeatLicense = async (beat, tier) => {
    setCheckingOut(beat.id + tier);
    try {
      const priceField = beat.on_sale && beat[`sale_price_${tier}`] ? `sale_price_${tier}` : `license_${tier}`;
      const { data } = await axios.post(`${API}/beats/checkout`, {
        beat_id: beat.id,
        license_tier: tier,
        price: beat[priceField]
      });
      if (data.url) window.location.href = data.url;
    } catch {
      toast.error('Checkout failed');
    }
    setCheckingOut(null);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
        <div className="flex items-center gap-4 mb-10">
          <Link to="/" data-testid="back-home-link">
            <Button variant="outline" className="rounded-none border-[#262626] hover:border-[#FF3B30]">
              <ArrowLeft className="w-4 h-4 mr-2" /> BACK
            </Button>
          </Link>
          <div>
            <p className="overline text-[#FF3B30] mb-1 text-xs tracking-widest font-bold">PRODUCERS</p>
            <h1 className="font-cabinet text-3xl sm:text-4xl font-black uppercase tracking-tighter" data-testid="beats-page-title">
              {siteSettings?.section_titles?.beats || 'SLAP SECTION'}
            </h1>
          </div>
        </div>

        <div className="space-y-4">
          {(() => {
            const featured = beats.filter(b => b.home_featured).sort((a,b)=>(a.home_featured_order??0)-(b.home_featured_order??0)).slice(0, 6);
            if (featured.length === 0) return null;
            return (
              <div className="mb-8" data-testid="beats-featured-hero">
                <p className="overline text-[#DAA520] mb-3 text-[10px] tracking-widest font-bold">★ FEATURED</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {featured.map(b => (
                    <div key={`hero-${b.id}`} className="aspect-square bg-[#0A0A0A] border border-[#DAA520]/30 hover:border-[#DAA520] overflow-hidden relative group" data-testid={`featured-beat-${b.id}`}>
                      {b.cover_image
                        ? <img src={b.cover_image} alt={b.title} className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center text-[#262626] text-xs">{b.title}</div>}
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/70 to-transparent p-2">
                        <p className="text-[11px] font-bold text-white truncate">{b.title}</p>
                        <p className="text-[9px] text-[#A3A3A3] truncate">{b.producer || ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
          {beats.map(beat => {
            const status = beat.sale_status || 'for_sale';
            const isLeaseOnly = status === 'lease_only';
            const isNotForSale = status === 'not_for_sale';
            return (
              <div key={beat.id} className="bg-[#141414] border border-[#262626] p-5 sm:p-6" data-testid={`beat-${beat.id}`}>
                <div className="flex flex-col gap-4">
                  <div className="flex items-start gap-4">
                    {beat.cover_image && (
                      <img src={toFullUrl(beat.cover_image)} alt={beat.title} className="w-14 h-14 object-cover border border-[#262626] flex-shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <h3 className="font-cabinet font-bold text-lg uppercase truncate">{beat.title}</h3>
                      <p className="text-[#A3A3A3] text-sm truncate">{beat.producer}{beat.genre ? ` · ${beat.genre}` : ''}</p>
                      <div className="flex flex-wrap gap-3 text-xs text-[#666] mt-1">
                        {beat.bpm && <span>{beat.bpm} BPM</span>}
                        {beat.key && <span>{beat.key}</span>}
                        {isLeaseOnly && <span className="text-[#DAA520] font-bold">LEASE ONLY</span>}
                      </div>
                    </div>
                  </div>
                  {beat.audio_preview && <BeatPlayer beatId={beat.id} src={normalizeAudioUrl(beat.audio_preview)} />}
                  <div className="flex flex-wrap items-center gap-3">
                    {isNotForSale ? (
                      <span className="px-4 py-2 bg-[#262626] text-[#666] text-sm font-bold uppercase">NOT FOR SALE</span>
                    ) : (
                      <div className="flex gap-3 flex-wrap">
                        {beat.on_sale && <span className="self-center text-xs font-bold text-[#DAA520] uppercase">Sale</span>}
                        {beat.license_basic > 0 && (
                          <button onClick={() => handleBeatLicense(beat, 'basic')}
                            className="px-4 py-2 border border-[#262626] hover:border-[#FF3B30] text-sm transition-colors" data-testid={`beat-basic-${beat.id}`}>
                            <span className="text-[#A3A3A3] text-xs block">BASIC</span>
                            {beat.on_sale && beat.sale_price_basic ? (
                              <><span className="font-mono text-[#A3A3A3] line-through text-xs">${beat.license_basic}</span> <span className="font-mono font-bold text-[#DAA520]">${beat.sale_price_basic}</span></>
                            ) : <span className="font-mono font-bold">${beat.license_basic}</span>}
                          </button>
                        )}
                        {beat.license_premium > 0 && (
                          <button onClick={() => handleBeatLicense(beat, 'premium')}
                            className="px-4 py-2 border border-[#262626] hover:border-[#FF3B30] text-sm transition-colors" data-testid={`beat-premium-${beat.id}`}>
                            <span className="text-[#A3A3A3] text-xs block">PREMIUM</span>
                            {beat.on_sale && beat.sale_price_premium ? (
                              <><span className="font-mono text-[#A3A3A3] line-through text-xs">${beat.license_premium}</span> <span className="font-mono font-bold text-[#DAA520]">${beat.sale_price_premium}</span></>
                            ) : <span className="font-mono font-bold">${beat.license_premium}</span>}
                          </button>
                        )}
                        {beat.license_exclusive > 0 && !isLeaseOnly && (
                          <button onClick={() => handleBeatLicense(beat, 'exclusive')}
                            className="px-4 py-2 bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] text-sm transition-colors" data-testid={`beat-exclusive-${beat.id}`}>
                            <span className="text-xs block opacity-80">EXCLUSIVE</span>
                            {beat.on_sale && beat.sale_price_exclusive ? (
                              <><span className="font-mono line-through text-xs opacity-60">${beat.license_exclusive}</span> <span className="font-mono font-bold text-[#DAA520]">${beat.sale_price_exclusive}</span></>
                            ) : <span className="font-mono font-bold">${beat.license_exclusive}</span>}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <CommentSection itemType="beat" itemId={beat.id} />
                  <div className="mt-2 flex items-center gap-4">
                    <LikeButton itemType="beat" itemId={beat.id} />
                    <FireButton itemType="beat" itemId={beat.id} />
                  </div>
                </div>
              </div>
            );
          })}

          {beats.length === 0 && (
            <div className="bg-[#141414] border border-[#262626] p-12 text-center">
              <Headphones className="w-12 h-12 mx-auto text-[#262626] mb-4" />
              <p className="text-[#A3A3A3]">No beats available yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
